import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, Clock, X } from 'lucide-react';
import type { Company } from '@gokrew/models/company';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/popover';
import styles from './CompanySwitcher.module.css';
import { SearchBar } from '@/components/SearchBar';
import { SwitcherCard } from './SwitcherCard';
import { formatShortcut, isMacPlatform } from '@/utility/shortcuts';
import {
  companyName,
  matchesCompanySearch,
  SWITCH_SHORTCUT,
} from './companyLabels';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { getColorForText } from '@/lib/utils';
import { getInitialsFromName } from '@/utility/utility';
import { AVATAR_COLORS } from '@/constants/avatar-colors';

export const LAST_COMPANY_KEY = 'last_company_id';

const SHORTCUT_CODE = 'KeyJ';

const isShortcutKey = (event: KeyboardEvent) => event.code === SHORTCUT_CODE;

export interface CompanySwitcherProps {
  companies: Company[];
  selectedCompanyId: string;
  onCompanyChange: (company: Company) => void;
  isCollapsed?: boolean;
  className?: string;
  flat?: boolean;
}

export const CompanySwitcher = ({
  companies,
  selectedCompanyId,
  onCompanyChange,
  isCollapsed = false,
  className,
  flat = false,
}: CompanySwitcherProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [brokenLogos, setBrokenLogos] = useState<Set<string>>(new Set());
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const listRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [anchorHeight, setAnchorHeight] = useState(0);

  const openPanel = useCallback(() => {
    setAnchorHeight(cardRef.current?.offsetHeight ?? 0);
    setOpen(true);
  }, []);

  const safeGetItem = (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const safeSetItem = (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode / quota — the panel still works without the record */
    }
  };

  const selected =
    companies.find((c) => c.id === selectedCompanyId) ?? companies[0];
  const lastSelectedId = safeGetItem(LAST_COMPANY_KEY);
  const lastSelected =
    lastSelectedId && lastSelectedId !== selectedCompanyId
      ? companies.find((c) => c.id === lastSelectedId)
      : null;

  const handleSelect = (company: Company) => {
    safeSetItem(LAST_COMPANY_KEY, selectedCompanyId);
    onCompanyChange(company);
    setOpen(false);
    setSearch('');
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  const handlePanelKeyDown = useCallback((event: ReactKeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      target.getAttribute('role') === 'button'
    ) {
      event.preventDefault();
      target.click();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role="button"]') ?? []
    );
    if (!items.length) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const index = items.indexOf(document.activeElement as HTMLElement);
    const next =
      index === -1
        ? step === 1
          ? 0
          : items.length - 1
        : (index + step + items.length) % items.length;
    items[next].focus();
  }, []);

  const markLogoBroken = (companyId: string) =>
    setBrokenLogos((prev) => new Set(prev).add(companyId));

  const hasLogo = (company: Company) =>
    Boolean(company.logo) && !brokenLogos.has(company.id);

  const query = search.trim().toLowerCase();

  const filteredCompanies = query
    ? companies.filter((c) => matchesCompanySearch(c, query))
    : companies;

  const filteredLastSelected =
    lastSelected && filteredCompanies.find((c) => c.id === lastSelected.id)
      ? lastSelected
      : null;

  const filteredCurrent =
    filteredCompanies.find((c) => c.id === selected?.id) ?? null;

  const switchToCompanies = filteredCompanies.filter(
    (company) =>
      company.id !== filteredCurrent?.id &&
      (query || !filteredLastSelected || company.id !== filteredLastSelected.id)
  );

  const highlights = [
    filteredCurrent,
    query ? null : filteredLastSelected,
  ].filter((company): company is Company => Boolean(company));

  const setupMarquee = useCallback(() => {
    if (!listRef.current) return;
    listRef.current
      .querySelectorAll<HTMLElement>(`.${styles.nameWrap}`)
      .forEach((wrap) => {
        const nameEl = wrap.querySelector<HTMLElement>(`.${styles.name}`);
        if (!nameEl) return;
        const overflow = nameEl.scrollWidth - wrap.clientWidth;
        if (overflow > 2) {
          wrap.classList.remove(styles.nameWrapFits);
          wrap.style.setProperty('--shift', `${overflow + 8}px`);
          nameEl.style.setProperty(
            '--dur',
            `${Math.max(0.9, (overflow + 8) / 80).toFixed(2)}s`
          );
        } else {
          wrap.classList.add(styles.nameWrapFits);
        }
      });
  }, []);

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(setupMarquee);
  }, [open, filteredCompanies, query, setupMarquee]);

  /** Always rendered — a company with no headcount reads as 0, never blank. */
  const companySubtitle = (company: Company) =>
    t('company_switcher.member_count', { count: company.employeeCount ?? 0 });

  const renderAvatar = (company: Company, avatarClass: string) => (
    <span
      className={avatarClass}
      style={
        {
          '--av-color': getColorForText(companyName(company), AVATAR_COLORS),
        } as CSSProperties
      }
    >
      {hasLogo(company) ? (
        <img
          src={company.logo ?? ''}
          alt=""
          aria-hidden="true"
          className={styles.avImg}
          onError={() => markLogoBroken(company.id)}
        />
      ) : (
        getInitialsFromName(companyName(company))
      )}
    </span>
  );

  const multiCompany = companies.length > 1;

  const isMac = isMacPlatform();
  const shortcutHint = formatShortcut(SWITCH_SHORTCUT, isMac);

  useEffect(() => {
    if (!multiCompany) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.defaultPrevented) return;
      if (!isShortcutKey(event)) return;
      if (event.altKey || event.shiftKey) return;
      const primaryHeld = isMac ? event.metaKey : event.ctrlKey;
      const otherHeld = isMac ? event.ctrlKey : event.metaKey;
      if (!primaryHeld || otherHeld) return;

      event.preventDefault();
      setOpen((prev) => {
        if (prev) {
          setSearch('');
          return false;
        }
        setAnchorHeight(cardRef.current?.offsetHeight ?? 0);
        return true;
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [multiCompany, isMac]);

  const card = (
    <SwitcherCard
      ref={cardRef}
      companies={companies}
      selected={selected}
      isCollapsed={isCollapsed}
      multiCompany={multiCompany}
      shortcutHint={shortcutHint}
      className={className}
      flat={flat}
      onOpen={openPanel}
      hasLogo={hasLogo}
      onLogoError={markLogoBroken}
    />
  );

  if (!multiCompany) return card;

  return (
    <>
      {open &&
        createPortal(
          <div className={styles.overlay} onClick={() => setOpen(false)} />,
          document.body
        )}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>{card}</PopoverAnchor>

        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={-anchorHeight}
          className={styles.panel}
          onKeyDown={handlePanelKeyDown}
        >
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>
              {t('company_switcher.switch')}
            </span>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={() => handleOpenChange(false)}
              aria-label={t('company_switcher.close')}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>

          <SearchBar
            placeholder={t('company_switcher.search_placeholder')}
            searchKey="company"
            debounce={300}
            defaultValue={search}
            updateParams={(params) => {
              setSearch(params.company || '');
            }}
            autoFocus
            className={styles.searchInput}
          />

          <div ref={listRef} className={styles.scroll}>
            {filteredCompanies.length === 0 ? (
              <div className={styles.emptyState}>
                {t('company_switcher.no_results')}
              </div>
            ) : (
              <>
                {highlights.length > 0 && (
                  <div className={styles.highlightGrid}>
                    {highlights.map((company) => {
                      const isCurrent = company.id === selected?.id;
                      const subtitle = companySubtitle(company);

                      return (
                        <div
                          key={company.id}
                          className={`${styles.highlightCard} ${
                            isCurrent ? styles.highlightCardCurrent : ''
                          }`}
                          onClick={
                            isCurrent ? undefined : () => handleSelect(company)
                          }
                          role={isCurrent ? undefined : 'button'}
                          tabIndex={isCurrent ? undefined : 0}
                        >
                          {renderAvatar(company, styles.highlightAv)}

                          <div className={styles.info}>
                            <div className={styles.nameWrap}>
                              <span className={styles.name}>
                                {companyName(company)}
                              </span>
                            </div>
                            <span className={styles.rowSub}>{subtitle}</span>
                          </div>

                          <span
                            className={`${styles.highlightBadge} ${
                              isCurrent ? styles.badgeCheck : styles.badgeRecent
                            }`}
                            title={t(
                              isCurrent
                                ? 'company_switcher.current'
                                : 'company_switcher.recent'
                            )}
                          >
                            {isCurrent ? (
                              <Check size={10} strokeWidth={3} />
                            ) : (
                              <Clock size={13} />
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {switchToCompanies.length > 0 && (
                  <>
                    <div className={styles.sectionLabel}>
                      {query
                        ? t('company_switcher.results')
                        : t('company_switcher.switch_to')}
                    </div>
                    <div>
                      {switchToCompanies.map((company) => (
                        <div
                          key={company.id}
                          className={styles.companyRow}
                          onClick={() => handleSelect(company)}
                          role="button"
                          tabIndex={0}
                        >
                          {renderAvatar(company, styles.av)}

                          <div className={styles.info}>
                            <div className={styles.nameWrap}>
                              <span className={styles.name}>
                                {companyName(company)}
                              </span>
                            </div>
                            <span className={styles.rowSub}>
                              {companySubtitle(company)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
};
